const ROLE_LABELS = {
  customer: 'Customer',
  vendor: 'Vendor',
  mechanics: 'Mechanic',
  superadmin: 'Super Admin',
  admin: 'Admin',
  garage: 'Garage',
  shipping: 'Delivery',
};

/** Resolve name from user (name, fullName, displayName, firstName+lastName). */
function resolveUserName(user) {
  if (!user) return '';
  const name = (user.name || user.fullName || user.displayName || '').trim();
  if (name) return name;
  const first = (user.firstName || '').trim();
  const last = (user.lastName || '').trim();
  return [first, last].filter(Boolean).join(' ').trim() || '';
}

/**
 * Returns the display name for profile identity. Uses login person's name only.
 * Does not show "User 9087654321" or other generated names; returns '' so UI can show — or role.
 */
export function getDisplayName(user) {
  if (!user) return '';
  const name = resolveUserName(user);
  if (!name || name.includes('@') || /^User\s*\d+$/i.test(name) || /^phone[_\-]?\d*@/i.test(name)) return '';
  return name;
}

/** For UI only: real name only. No generated/placeholder names. Missing data returns '' so UI can show — or null. */
export function getDisplayNameForUI(user) {
  const name = getDisplayName(user);
  if (name) return name;
  const resolved = resolveUserName(user);
  if (resolved && isPlaceholderName(resolved)) return '';
  return '';
}

/** Returns formatted role label for profile identity. Uses user.role from API. */
export function getDisplayRole(user) {
  if (!user) return '';
  const r = (user.role || user.roleType || '').toString().trim().toLowerCase();
  if (!r) return '';
  return ROLE_LABELS[r] || (r.charAt(0).toUpperCase() + r.slice(1));
}

/** True if email looks generated/nonsense (e.g. phone_xxx@speralo.phone). Leave blank instead. Do not show. */
function isPlaceholderEmail(email) {
  if (!email || typeof email !== 'string') return true;
  const e = email.trim();
  if (!e) return true;
  if (/@speralo\.phone$/i.test(e)) return true;
  if (/@speralo\.facebook$/i.test(e)) return true;
  if (/^phone[_\-]?\d*@/i.test(e)) return true;
  if (/^fb[_\-]?\d*@/i.test(e)) return true;
  if (/^user\s*\d+@/i.test(e)) return true;
  if (/^superadmin[_\-]?\d*@/i.test(e)) return true;
  return false;
}

/** True if name looks generated (e.g. User 9087654321). Leave blank in form. */
function isPlaceholderName(name) {
  if (!name || typeof name !== 'string') return true;
  const n = name.trim();
  if (!n) return true;
  if (/^User\s*\d+$/i.test(n)) return true;
  if (n.includes('@')) return true;
  return false;
}

/** Email for profile forms/cards. Blank if not set or generated. No nonsense. */
export function getProfileDisplayEmail(user) {
  if (!user || !user.email) return '';
  return isPlaceholderEmail(user.email) ? '' : (user.email || '').trim();
}

/** Name for profile forms/cards. Blank if not set or generated. No nonsense. */
export function getProfileDisplayName(user) {
  const name = resolveUserName(user);
  if (!name || isPlaceholderName(name)) return '';
  return name;
}

/** Phone for profile forms/cards. Blank if not set. */
export function getProfileDisplayPhone(user) {
  if (!user) return '';
  const p = (user.phone || '').toString().trim();
  return p || '';
}

/** Mask phone for account UI — never show full number (B-10A AC 101). */
export function maskPhoneForDisplay(phone) {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  const local = digits.length > 10 ? digits.slice(-10) : digits;
  if (local.length < 4) return '+91 XXXX-XX-XXXX';
  return `+91 XXXX-XX-${local.slice(-4)}`;
}

/** Mask registration — last 4 characters visible. */
export function maskRegistration(reg) {
  if (!reg) return null;
  const s = String(reg).trim().toUpperCase();
  if (s.length <= 4) return s;
  return `${'X'.repeat(s.length - 4)}${s.slice(-4)}`;
}
